import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ExternalLinkIcon, PlusIcon } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { PillTabs } from '../components/ui/Tabs';
import { ButtonLink } from '../components/ui/Button';
import { SearchInput } from '../components/ui/SearchInput';
import { Pagination, Table, TD, TH, THead, TR } from '../components/ui/Table';
import { ClaimsTable } from '../components/claims/ClaimsTable';
import { StudentPanel } from '../components/panels/StudentPanel';
import { NotFound } from './NotFound';
import { schoolBySlug } from '../data/district';
import { studentsForSchool } from '../data/students';
import { claims } from '../data/claims';
import { usePagination } from '../hooks/usePagination';
import { Student } from '../types';
const tabs = [{
  value: 'students',
  label: 'Students'
}, {
  value: 'claims',
  label: 'Claims'
}];
export function SchoolDetail() {
  const {
    slug,
    tab = 'students'
  } = useParams();
  const navigate = useNavigate();
  const school = schoolBySlug(slug);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Student | null>(null);
  const rows = useMemo(() => {
    if (!school) return [];
    const q = query.trim().toLowerCase();
    return studentsForSchool(school.slug).filter(student => `${student.name} ${student.email} ${student.studentId}`.toLowerCase().includes(q));
  }, [school, query]);
  const {
    page,
    setPage,
    totalPages,
    pageItems
  } = usePagination(rows, 20);
  if (!school) {
    return <NotFound data-id="element-570" data-mp="2vata2hx2uocs" />;
  }

  // Staging wrongly lists every district claim here; the prototype scopes it to this school.
  const schoolClaims = claims.filter(claim => claim.schoolName === school.name);
  return <>
      <PageHeader title={school.name} inlineTabs={<PillTabs label="School sections" options={tabs} value={tab} onChange={next => navigate(`/overview/${school.slug}/${next}`)} data-id="element-572" data-mp="wv6vm3xlz8yt" />} actions={<>
            <ButtonLink to="/claims/create" data-id="element-573" data-mp="30z1pvxzeqs1z">
              <PlusIcon className="h-4 w-4" data-id="element-574" data-mp="35xmj3dg8sbgx" />
              File a Claim
            </ButtonLink>
            <ButtonLink to="/devices/create" variant="secondary" data-id="element-575" data-mp="3awpmktgkq9ms">
              <PlusIcon className="h-4 w-4" data-id="element-576" data-mp="3ay0riwlr9nfh" />
              Add Device
            </ButtonLink>
          </>} data-id="element-571" data-mp="10x1uz2vzbc9y" />

      <div className="flex flex-1 flex-col bg-white px-6 py-5" data-id="element-577" data-mp="3ewggv75b77w2">
        {tab === 'claims' ? <ClaimsTable claims={schoolClaims} data-id="element-578" data-mp="3jv1agnq22tbj" /> : <div className="flex flex-1 flex-col" data-id="element-579" data-mp="3opfbp6refuo4">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3" data-id="element-580" data-mp="3tmxkmx5f29w4">
              <SearchInput value={query} onChange={setQuery} placeholder="Search students by name, email or device ID" className="w-[290px]" data-id="element-581" data-mp="3tp1lt7bxkusp" />
              <button type="button" className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ink-700 hover:text-brand-600" data-id="element-582" data-mp="1r0a0egeubw2b">
                Export List
                <ExternalLinkIcon className="h-3.5 w-3.5" data-id="element-583" data-mp="1hz27kdv3fnfm" />
              </button>
            </div>

            <Table minWidth={900} data-id="element-584" data-mp="1d1k7ze2yukka">
              <THead data-id="element-585" data-mp="1d2lgbaslksyb">
                <tr data-id="element-586" data-mp="1d33jyshla9g9">
                  <TH data-id="element-587" data-mp="2ap0yyfwrfwng">Student Name</TH>
                  <TH data-id="element-588" data-mp="2anzn497x764o">Student Email</TH>
                  <TH data-id="element-589" data-mp="25pfz839rnk9o">Student ID</TH>
                </tr>
              </THead>
              <tbody data-id="element-590" data-mp="25rj0jjgdcztp">
                {pageItems.length === 0 && <TR data-id="element-591" data-mp="20xer8tjmsfpg">
                    <TD colSpan={3} className="py-10 text-center text-ink-500" data-id="element-592" data-mp="20wvbnjhge3el">
                      No students found
                    </TD>
                  </TR>}
                {pageItems.map(student => <TR key={student.id} onClick={() => setSelected(student)} data-id="element-593" data-mp="2ook738pkn7zr">
                    <TD data-id="element-594" data-mp="2om7iwdt801e9">{student.name}</TD>
                    <TD data-id="element-595" data-mp="296110k8kz1x0">{student.email}</TD>
                    <TD data-id="element-596" data-mp="294zgzq9l7535">{student.studentId}</TD>
                  </TR>)}
              </tbody>
            </Table>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} data-id="element-597" data-mp="1z835dboe62ud" />
          </div>}
      </div>

      <StudentPanel student={selected} onClose={() => setSelected(null)} data-id="element-598" data-mp="246o579zqekia" />
    </>;
}